import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import projectService from '../../services/projectService';
import type { MyReviewRequest } from '../../types/projects';
import { ProjectStatusPill } from '../Projects/ProjectBadges';
import { formatDateTime, timeAgo } from '../Projects/projectFormat';
import { RequestStateBadge } from '../Projects/workflow/ReviewRequestsCard';
import { personName } from '../Projects/workflow/workflowFormat';

const LIMIT = 6;

/** Dashboard widget « Avis demandés »: the reviews asked of the caller, pending first (plan §9.5, lot L3). */
export const ReviewRequestsWidget: React.FC = () => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<MyReviewRequest[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    projectService
      .myReviewRequests()
      .then((list) => {
        if (cancelled) return;
        // Newest first from the API; a stable sort keeps that order inside each group.
        const sorted = [...list].sort((a, b) => Number(a.answered) - Number(b.answered));
        setRequests(sorted.slice(0, LIMIT));
      })
      .catch(() => {
        if (!cancelled) setRequests([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-foreground">{t('projects.widgets.reviewRequests')}</h3>
        <Link to="/projets" className="text-sm text-primary hover:underline">
          {t('projects.widgets.seeAll')}
        </Link>
      </div>
      {requests === null ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('projects.loading')}
        </p>
      ) : requests.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('projects.widgets.reviewRequestsEmpty')}</p>
      ) : (
        <ul className="divide-y divide-border">
          {requests.map((r) => {
            const by = personName(r.requested_by);
            return (
              <li key={r.id}>
                <Link to={`/projets/${r.project_id}`} className="flex items-center gap-3 rounded-md py-2.5 hover:bg-muted/40">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">{r.project_title}</span>
                    <span className="block truncate text-xs text-muted-foreground" title={formatDateTime(r.created_at)}>
                      {[by ? t('projects.widgets.requestedBy', { name: by }) : '', timeAgo(r.created_at)]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <RequestStateBadge answered={r.answered} />
                  <ProjectStatusPill status={r.project_status} />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
